import React from "react";

const DEFAULT_DURATION = 30;

export const getNextNonSkippedTabId = (sortedTabs, currentTabId) => {
  const eligible = sortedTabs.filter(t => !t.get("skipInPresentation", false));
  if (eligible.size === 0) return null;
  const currentIdx = eligible.findIndex(t => t.get("id") === currentTabId);
  const nextIdx = (currentIdx + 1) % eligible.size;
  return eligible.getIn([nextIdx, "id"]);
};

// Returns secondsRemaining for display in the tab label.
//
// `isActiveTabLoaded` gates the countdown: while the active tab is still loading (which
// includes performance-snapshot cells — see app.js load tracking) the countdown is held
// so a tab is always shown fully rendered for its configured *minimum* duration and we
// never advance away from, or onto, a half-loaded tab.
const usePresentationTimer = (tabs, activeTabId, setActiveTabId, isPresenting, isActiveTabLoaded) => {
  const [secondsRemaining, setSecondsRemaining] = React.useState(null);

  // Read the latest load state inside the interval without re-creating it on every change.
  const isActiveTabLoadedRef = React.useRef(isActiveTabLoaded);
  isActiveTabLoadedRef.current = isActiveTabLoaded;

  React.useEffect(() => {
    if (!isPresenting) {
      setSecondsRemaining(null);
      return;
    }

    const sortedTabs = tabs.sortBy(t => t.get("position"));
    const activeTab = sortedTabs.find(t => t.get("id") === activeTabId);
    const duration = activeTab
        ? activeTab.get("presentationDuration", DEFAULT_DURATION)
        : DEFAULT_DURATION;

    setSecondsRemaining(duration);

    const id = setInterval(() => {
      // Hold the countdown until the current tab has finished loading.
      if (!isActiveTabLoadedRef.current) {
        return;
      }
      setSecondsRemaining(prev => {
        const next = prev - 1;
        if (next <= 0) {
          const nextId = getNextNonSkippedTabId(sortedTabs, activeTabId);
          if (nextId) setActiveTabId(nextId);
          return duration; // reset to full duration for the next tab
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [isPresenting, activeTabId, tabs, setActiveTabId]);

  return secondsRemaining;
};

export default usePresentationTimer;
